import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, Drawer, FormControlLabel, Link, Paper, Snackbar, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SendIcon from "@mui/icons-material/Send";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { AppLayout } from "@/components/AppLayout";
import {
  createInvoice, fetchBacklog, fetchTaskComments, sendReminder,
  type CollectionsBacklog, type CollectionsKind, type CollectionsRow, type TaskComments,
} from "@/api/collections";

// The Collections guide on learn.gofreedompower.com.
export const COLLECTIONS_GUIDE_URL =
  "https://learn.gofreedompower.com/Collections-Deposits-SOP-and-Final-Payments-3f9d90d9e24c81d38c51f0d5edaf7efa";

const REGIONS = ["All", "Austin", "Houston", "DFW", "Tampa", "Orlando"];

// Matches Collections::ReminderSend::COOLDOWN on the server, which is the real guard.
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

interface TileConfig { key: string; label: string; color: string }

const COLLECTIONS_TILE: TileConfig = { key: "collections_legal", label: "Collections / Legal", color: "#d32f2f" };

interface KindConfig {
  tab: string;
  title: string;
  task: string;
  invoice: string;
  description: string;
  tiles: TileConfig[];
  installDate?: boolean;
  downPayment?: boolean;
  sentFrom: string;
}

// One entry per Collections::BacklogService::KINDS. Tiles are the mutually
// exclusive buckets, in the order the server assigns them.
const KIND_CONFIG: Record<CollectionsKind, KindConfig> = {
  deposit: {
    tab: "Deposits",
    title: "Deposit Backlog",
    task: "Confirm Deposit Received",
    invoice: "deposit",
    description: 'Open "Confirm Deposit Received" tasks where a cash deposit is owed: cash and Maxwell Power (HDM) projects, plus financed projects with a cash down payment (or a deposit invoice already in Stripe). Ready and not on hold, or in Collections, Legal Issue or Uncollectible.',
    tiles: [{ key: "active", label: "Active", color: "#ed6c02" }, COLLECTIONS_TILE],
    downPayment: true,
    sentFrom: "your address",
  },
  sop: {
    tab: "SOP Payments",
    title: "SOP Payment Backlog",
    task: "Receive SOP Payment",
    invoice: "Start of Project",
    description: 'Open "Receive SOP Payment" tasks (Start of Project invoice) on cash and Maxwell Power (HDM) projects that are ready and not on hold, plus any on a project in Collections, Legal Issue or Uncollectible.',
    tiles: [
      { key: "not_scheduled", label: "Install Not Scheduled", color: "#ed6c02" },
      { key: "scheduled", label: "Install Scheduled", color: "#2e7d32" },
      COLLECTIONS_TILE,
    ],
    installDate: true,
    sentFrom: "your address",
  },
  final: {
    tab: "Final Payments",
    title: "Final Payment Backlog",
    task: "Receive Final Payment",
    invoice: "final",
    description: 'Open "Receive Final Payment" tasks on cash and Maxwell Power (HDM) projects (financed projects are paid by the lender), ready and not on hold, or in Collections, Legal Issue or Uncollectible.',
    tiles: [{ key: "active", label: "Active", color: "#ed6c02" }, COLLECTIONS_TILE],
    sentFrom: "customerservice@gofreedompower.com",
  },
};

const KINDS = Object.keys(KIND_CONFIG) as CollectionsKind[];

const BUCKET_LABELS: Record<string, string> = {
  not_scheduled: "Not scheduled",
  scheduled: "Scheduled",
  active: "Active",
  collections_legal: "Collections / Legal",
};

const STATUS_CHIPS: Record<string, { label: string; color: "warning" | "success" | "error" | "default" }> = {
  open: { label: "Unpaid", color: "warning" },
  paid: { label: "Paid (task open)", color: "success" },
  uncollectible: { label: "Uncollectible", color: "error" },
  void: { label: "Void", color: "default" },
  none: { label: "No invoice", color: "default" },
  unknown: { label: "Unknown", color: "default" },
};

const money = (v: number | null | undefined) =>
  v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const shortDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
const longDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

function errorMessage(e: unknown): string {
  const err = e as { response?: { data?: { error?: string }; status?: number }; message?: string };
  return err?.response?.data?.error ?? err?.message ?? "Something went wrong.";
}

const inCooldown = (row: CollectionsRow) => {
  const sentAt = row.last_reminder?.sent_at;
  return !!sentAt && Date.now() - new Date(sentAt).getTime() < COOLDOWN_MS;
};

// Why the Send button is disabled for a row, or null when it can be pressed.
function sendBlockedReason(row: CollectionsRow, canSend: boolean, stripeConfigured: boolean, invoiceLabel: string) {
  if (!canSend) return "You need the Send reminder access (Users page) to send reminders.";
  if (!stripeConfigured) return "Stripe is not connected yet, so the invoice can't be looked up.";
  if (row.invoice_status === "paid") return `The ${invoiceLabel} invoice is already paid.`;
  if (row.invoice_status !== "open" || !row.invoice_url) return `No open ${invoiceLabel} invoice in Stripe for this project.`;
  if (inCooldown(row)) {
    return `Already sent ${shortDate(row.last_reminder!.sent_at)} by ${row.last_reminder!.sent_by}. One reminder per 24 hours.`;
  }
  return null;
}

// Why Create invoice is disabled (shown only when Stripe has no live invoice for
// the stage), or null when it can be pressed.
function createBlockedReason(canCreate: boolean, stripeConfigured: boolean) {
  if (!canCreate) return "You need the Create invoice access (Users page) to create invoices.";
  if (!stripeConfigured) return "Stripe is not connected yet, so existing invoices can't be checked.";
  return null;
}

// Live comments on one payment task, from Sunrise (Collections::TaskComments).
function useTaskComments(taskId: string | undefined, kind: CollectionsKind) {
  return useQuery({
    queryKey: ["collections", "task_comments", kind, taskId],
    queryFn: () => fetchTaskComments(taskId!, kind),
    enabled: !!taskId,
    retry: false,
    staleTime: 0,
  });
}

type CommentsQuery = ReturnType<typeof useTaskComments>;

function CommentItemView({ comment, reply = false }: { comment: { author: string; at: string | null; text: string }; reply?: boolean }) {
  return (
    <Box sx={{ pl: reply ? 2 : 0, borderLeft: reply ? 2 : 0, borderColor: "divider", mt: reply ? 1 : 0 }}>
      <Typography variant="caption" color="text.secondary">
        <strong>{comment.author}</strong> · {longDate(comment.at)}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{comment.text || "(no text)"}</Typography>
    </Box>
  );
}

// Threads newest first, replies under each. Shared by the drawer and the Create
// invoice review.
function CommentThreads({ comments }: { comments: CommentsQuery }) {
  if (comments.isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>;
  if (comments.isError) {
    return (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => comments.refetch()}>Retry</Button>}>
        {errorMessage(comments.error) || "Couldn't load the task comments."}
      </Alert>
    );
  }
  const threads = (comments.data as TaskComments | undefined)?.threads ?? [];
  if (threads.length === 0) return <Typography variant="body2" color="text.secondary">No comments on this task.</Typography>;
  return (
    <>
      {threads.map((t, i) => (
        <Box key={t.id ?? i}>
          {i > 0 && <Divider sx={{ my: 1.5 }} />}
          <CommentItemView comment={t} />
          {t.replies.map((r, j) => <CommentItemView key={r.id ?? j} comment={r} reply />)}
        </Box>
      ))}
    </>
  );
}

// Count chip plus a preview of the newest comment (from the cached backlog).
function CommentsCell({ row, onOpen }: { row: CollectionsRow; onOpen: () => void }) {
  if (row.comment_count == null) {
    return (
      <Tooltip title="Couldn't load comments when the backlog was built. Open to load them now.">
        <Chip size="small" label="?" icon={<ChatBubbleOutlineIcon />} onClick={onOpen} variant="outlined" />
      </Tooltip>
    );
  }
  if (row.comment_count === 0) return <Typography variant="body2" color="text.disabled">—</Typography>;
  const latest = row.latest_comment;
  return (
    <Box onClick={onOpen} sx={{ cursor: "pointer", maxWidth: 240 }}>
      <Chip
        size="small"
        icon={<ChatBubbleOutlineIcon />}
        label={`${row.comment_count} comment${row.comment_count === 1 ? "" : "s"}`}
        variant="outlined"
        color="info"
        sx={{ cursor: "pointer" }}
      />
      {latest && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", mt: 0.5 }}
        >
          {latest.author} · {shortDate(latest.at)}: {latest.text}
        </Typography>
      )}
    </Box>
  );
}

// Earliest main-install job in Skedulo; falls back to the Sunrise projected date (grey).
function InstallDateCell({ row }: { row: CollectionsRow }) {
  if (row.install_date) {
    return (
      <Tooltip title={`Skedulo: ${row.install_job_type} · ${row.install_job_status}`}>
        <span>{longDate(row.install_date)}</span>
      </Tooltip>
    );
  }
  if (row.projected_install_date) {
    return (
      <Tooltip title="Projected install date from Sunrise (no install job in Skedulo)">
        <Typography component="span" variant="body2" color="text.disabled">{row.projected_install_date}</Typography>
      </Tooltip>
    );
  }
  return <Typography component="span" variant="body2" color="text.disabled">—</Typography>;
}

export function CollectionsPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const kindParam = searchParams.get("kind") as CollectionsKind | null;
  const kind: CollectionsKind = kindParam && KINDS.includes(kindParam) ? kindParam : "deposit";
  const regionParam = searchParams.get("region");
  const region = regionParam && REGIONS.includes(regionParam) ? regionParam : "All";
  const cfg = KIND_CONFIG[kind];

  const [bucketFilter, setBucketFilter] = useState<string | null>(null);
  const [confirmRow, setConfirmRow] = useState<CollectionsRow | null>(null);
  const [invoiceRow, setInvoiceRow] = useState<CollectionsRow | null>(null);
  const [commentsRow, setCommentsRow] = useState<CollectionsRow | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [toast, setToast] = useState<{ severity: "success" | "error"; message: string } | null>(null);

  const queryKey = ["collections", "backlog", kind, region];
  const backlog = useQuery({
    queryKey,
    queryFn: () => fetchBacklog(kind, region),
    // While the cache is building, poll until it lands; otherwise every 5 minutes.
    refetchInterval: (q) => (q.state.data?.loading || q.state.data?.refreshing ? 5000 : 5 * 60 * 1000),
  });
  const drawerComments = useTaskComments(commentsRow?.task_id, kind);
  const reviewComments = useTaskComments(invoiceRow?.task_id, kind);

  const setParam = (key: "kind" | "region", value: string, fallback: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === fallback) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
    setBucketFilter(null);
  };

  const updateRow = (projectId: string, patch: Partial<CollectionsRow>) =>
    qc.setQueryData<CollectionsBacklog>(queryKey, (prev) =>
      prev && { ...prev, rows: prev.rows.map((r) => (r.project_id === projectId ? { ...r, ...patch } : r)) });

  const remind = useMutation({
    mutationFn: (row: CollectionsRow) => sendReminder(row.project_id, kind),
    onSuccess: (result, row) => {
      updateRow(row.project_id, { last_reminder: result.last_reminder });
      setToast({ severity: "success", message: `Reminder queued for ${row.project_name} (${row.project_id}).` });
    },
    onError: (e) => setToast({ severity: "error", message: errorMessage(e) }),
    onSettled: () => setConfirmRow(null),
  });

  const invoice = useMutation({
    mutationFn: (row: CollectionsRow) => createInvoice(row.project_id, kind),
    onSuccess: (result, row) => {
      // Show the new link now; the backlog rebuild fills in the number and amount.
      updateRow(row.project_id, { invoice_url: result.hosted_invoice_url, invoice_status: "open", invoice_number: null, amount_remaining: null });
      setToast({ severity: "success", message: `Created and emailed the ${cfg.invoice} invoice for ${row.project_name} (${row.project_id}).` });
    },
    onError: (e) => setToast({ severity: "error", message: errorMessage(e) }),
    onSettled: () => setInvoiceRow(null),
  });

  // Create stays off until the comments have loaded and, when there are any,
  // the presser has ticked that they read them.
  const reviewCount = reviewComments.data?.count ?? 0;
  const commentsCleared = !!reviewComments.data && (reviewCount === 0 || reviewed);

  const data = backlog.data;
  const building = !!data?.loading;
  const buildError = data?.build_error ?? null;
  const neverBuilt = !!buildError && !data?.as_of;
  const summary = data?.summary ?? {};
  const rows = data?.rows ?? [];
  const visibleRows = bucketFilter ? rows.filter((r) => r.bucket === bucketFilter) : rows;
  const stripeConfigured = data?.stripe_configured !== false;
  const canSend = !!data?.can_send_reminder;
  const canCreate = !!data?.can_create_invoice;
  const tileWidth = { xs: 12, sm: 6, md: 12 / (cfg.tiles.length + 1) };

  // Region, lender and bucket ride under the project and stage; invoice status and
  // amount share one cell; the last reminder sits under its button.
  const columns = [
    "Project", "Stage",
    ...(cfg.installDate ? ["Install"] : []),
    ...(cfg.downPayment ? ["Down pmt"] : []),
    "Age (d)", "Invoice", "Comments", "Action",
  ];

  const tileSx = (active: boolean, color: string) => ({
    p: 2,
    textAlign: "center",
    border: active ? `2px solid ${color}` : `1px solid ${color}`,
    backgroundColor: active ? `${color}14` : "background.paper",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": { backgroundColor: `${color}14` },
  });

  return (
    <AppLayout maxWidth="xl">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <ReceiptLongIcon color="secondary" sx={{ fontSize: 28 }} />
        <Typography variant="h5" sx={{ flex: 1 }}>Collections</Typography>
        <Button size="small" startIcon={<MenuBookIcon />} href={COLLECTIONS_GUIDE_URL} target="_blank" rel="noopener">
          Guide
        </Button>
      </Box>

      <Tabs
        value={kind}
        onChange={(_e, v: CollectionsKind) => setParam("kind", v, "deposit")}
        textColor="secondary"
        indicatorColor="secondary"
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        {KINDS.map((k) => <Tab key={k} value={k} label={KIND_CONFIG[k].tab} />)}
      </Tabs>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>{cfg.title}</Typography>
          {data?.as_of && (
            <Typography variant="caption" color="text.secondary">
              {data.refreshing ? "Refreshing… " : ""}as of {new Date(data.as_of).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          {cfg.description} Invoice links and paid status come from Stripe.
        </Typography>

        <Tabs
          value={region}
          onChange={(_e, v: string) => setParam("region", v, "All")}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 3, minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 1, minWidth: 80 } }}
        >
          {REGIONS.map((r) => <Tab key={r} label={r} value={r} />)}
        </Tabs>

        {backlog.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(backlog.error)}</Alert>}
        {data && !stripeConfigured && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Stripe isn't connected (FSP_STRIPE_API_URL / FSP_STRIPE_API_TOKEN), so invoice links, paid status and
            reminders are unavailable.
          </Alert>
        )}

        {buildError && (
          <Alert severity={neverBuilt ? "error" : "warning"} sx={{ mb: 2 }}>
            {neverBuilt
              ? `Couldn't build the ${cfg.tab} list: ${buildError.message}`
              : `The last refresh failed (${buildError.message}), so this list may be out of date.`}{" "}
            It retries on its own every few minutes.
          </Alert>
        )}

        {(backlog.isLoading || building) && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
            <CircularProgress />
            {building && <Typography variant="body2" color="text.secondary">Building the backlog (checking Stripe)…</Typography>}
          </Box>
        )}

        {!backlog.isLoading && !building && !neverBuilt && data && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={tileWidth}>
                <Paper variant="outlined" onClick={() => setBucketFilter(null)} sx={tileSx(bucketFilter === null, "#f98a3c")}>
                  <Typography variant="h4" sx={{ color: "#f98a3c", fontWeight: "bold" }}>{summary.total ?? 0}</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{money(summary.unpaid_amount)} unpaid</Typography>
                  <Typography variant="body2" color="text.secondary">Open Tasks</Typography>
                </Paper>
              </Grid>
              {cfg.tiles.map((t) => (
                <Grid key={t.key} size={tileWidth}>
                  <Paper
                    variant="outlined"
                    onClick={() => setBucketFilter((prev) => (prev === t.key ? null : t.key))}
                    sx={tileSx(bucketFilter === t.key, t.color)}
                  >
                    <Typography variant="h4" sx={{ color: t.color, fontWeight: "bold" }}>{summary[t.key] ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary">{t.label}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {(summary.paid_task_open ?? 0) > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {summary.paid_task_open} project{summary.paid_task_open === 1 ? " is" : "s are"} paid in Stripe but the
                "{cfg.task}" task is still open in Sunrise.
              </Alert>
            )}

            <TableContainer>
              <Table size="small" stickyHeader sx={{ "& td, & th": { px: 1 } }}>
                <TableHead>
                  <TableRow>
                    {columns.map((h) => <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>No open tasks here.</TableCell>
                    </TableRow>
                  )}
                  {visibleRows.map((row) => {
                    const chip = STATUS_CHIPS[row.invoice_status] ?? STATUS_CHIPS.unknown;
                    const blocked = sendBlockedReason(row, canSend, stripeConfigured, cfg.invoice);
                    const createBlocked = createBlockedReason(canCreate, stripeConfigured);
                    return (
                      <TableRow key={row.project_id} hover>
                        <TableCell>
                          <Link href={row.sunrise_url} target="_blank" rel="noopener">{row.project_name || row.project_id}</Link>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {row.project_id} · {row.region || "—"} · {row.lender || "No lender"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.stage || "—"}
                          <Tooltip title={row.scheduled_by === "task" ? "Schedule Installation is complete" : row.scheduled_by === "date" ? `Projected install ${row.projected_install_date}` : ""}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{BUCKET_LABELS[row.bucket]}</Typography>
                          </Tooltip>
                        </TableCell>
                        {cfg.installDate && <TableCell sx={{ whiteSpace: "nowrap" }}><InstallDateCell row={row} /></TableCell>}
                        {cfg.downPayment && <TableCell>{row.down_payment ? money(row.down_payment) : "—"}</TableCell>}
                        <TableCell>{row.task_age_days ?? "—"}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
                            {row.invoice_url && (
                              <Link href={row.invoice_url} target="_blank" rel="noopener" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mr: 0.5 }}>
                                {row.invoice_number || "Stripe"} <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </Link>
                            )}
                            <Chip size="small" label={chip.label} variant="outlined" color={chip.color} />
                            {row.open_invoice_count > 1 && (
                              <Tooltip title={`${row.open_invoice_count} open ${cfg.invoice} invoices in Stripe (installments or re-issues). The amount is the total; the link is the newest.`}>
                                <Chip size="small" color="warning" label={`${row.open_invoice_count} open`} />
                              </Tooltip>
                            )}
                            {row.other_open_count > 0 && (
                              <Tooltip title={`Also ${row.other_open_count} other open invoice${row.other_open_count === 1 ? "" : "s"} on this project (${money(row.other_open_amount)}), e.g. a payment plan, that don't name a payment stage.`}>
                                <Chip size="small" color="info" label={`+${row.other_open_count} other`} />
                              </Tooltip>
                            )}
                          </Box>
                          {row.invoice_status === "open" && row.amount_remaining != null && (
                            <Typography variant="caption" sx={{ display: "block" }}>{money(row.amount_remaining)} remaining</Typography>
                          )}
                        </TableCell>
                        <TableCell><CommentsCell row={row} onOpen={() => setCommentsRow(row)} /></TableCell>
                        <TableCell>
                          {row.invoice_status === "none" ? (
                            <Tooltip title={createBlocked || `Create the ${cfg.invoice} invoice in Stripe and email it to the customer`}>
                              <span>
                                <Button
                                  size="small" variant="outlined" color="info" startIcon={<NoteAddIcon />}
                                  disabled={!!createBlocked}
                                  onClick={() => { setReviewed(false); setInvoiceRow(row); }}
                                  sx={{ whiteSpace: "nowrap" }}
                                >
                                  Create invoice
                                </Button>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title={blocked || `Email the customer their ${cfg.invoice} invoice link`}>
                              <span>
                                <Button
                                  size="small" variant="outlined" color="secondary" startIcon={<SendIcon />}
                                  disabled={!!blocked}
                                  onClick={() => setConfirmRow(row)}
                                  sx={{ whiteSpace: "nowrap" }}
                                >
                                  Send reminder
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {row.last_reminder && (
                            <Tooltip title={row.last_reminder.sent_by}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                Last sent {shortDate(row.last_reminder.sent_at)}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      <Dialog open={!!confirmRow} onClose={() => !remind.isPending && setConfirmRow(null)}>
        <DialogTitle>Send {cfg.invoice} reminder?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This emails <strong>{confirmRow?.project_name}</strong> (project {confirmRow?.project_id}) the {cfg.invoice} invoice
            link{confirmRow?.amount_remaining != null ? ` for ${money(confirmRow.amount_remaining)}` : ""}.
            It is sent from {cfg.sentFrom} and logged to HubSpot and the Sunrise pulse.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)} disabled={remind.isPending}>Cancel</Button>
          <Button
            onClick={() => confirmRow && remind.mutate(confirmRow)}
            disabled={remind.isPending}
            variant="contained"
            startIcon={remind.isPending ? <CircularProgress size={16} /> : <SendIcon />}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!invoiceRow} onClose={() => !invoice.isPending && setInvoiceRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Create {cfg.invoice} invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This creates the {cfg.invoice} invoice for <strong>{invoiceRow?.project_name}</strong> (project {invoiceRow?.project_id}) in
            Stripe and emails it to the customer, the same as the Sunrise invoice button. The amount comes from the
            project's payment schedule. It is refused if Stripe already has an open or paid {cfg.invoice} invoice.
          </DialogContentText>
          {/* Holds are written as task comments; read them before billing. */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Comments on the "{cfg.task}" task</Typography>
          <Box sx={{ maxHeight: 280, overflowY: "auto", p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <CommentThreads comments={reviewComments} />
          </Box>
          {reviewCount > 0 && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />}
              label={`I've reviewed the ${reviewCount} comment${reviewCount === 1 ? "" : "s"} on this task`}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceRow(null)} disabled={invoice.isPending}>Cancel</Button>
          <Button
            onClick={() => invoiceRow && invoice.mutate(invoiceRow)}
            disabled={invoice.isPending || !commentsCleared}
            variant="contained"
            startIcon={invoice.isPending ? <CircularProgress size={16} /> : <NoteAddIcon />}
          >
            Create and email
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={!!commentsRow}
        onClose={() => setCommentsRow(null)}
        slotProps={{ paper: { sx: { width: { xs: "100%", sm: 420 }, p: 2 } } }}
      >
        <Typography variant="h6">{commentsRow?.project_name}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          "{cfg.task}" task comments ·{" "}
          <Link href={commentsRow?.sunrise_url} target="_blank" rel="noopener">Open in Sunrise</Link>
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <CommentThreads comments={drawerComments} />
      </Drawer>

      {toast && (
        <Snackbar open autoHideDuration={6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: "100%" }}>{toast.message}</Alert>
        </Snackbar>
      )}
    </AppLayout>
  );
}
